import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const CATEGORIES = ['vegetable','fruit','grain','spice','flower','other'];
const SUB_CATS = {
  vegetable:['Tomato','Carrot','Brinjal','Potato','Onion','Cabbage','Spinach','Chilli','Beetroot','Peas'],
  fruit:    ['Mango','Banana','Guava','Papaya','Watermelon','Grapes','Pomegranate','Orange','Apple','Lemon'],
  grain:    ['Rice','Wheat','Maize','Jowar','Bajra','Ragi','Sorghum','Barley'],
  spice:    ['Turmeric','Ginger','Garlic','Pepper','Cardamom','Coriander','Cumin','Fenugreek'],
  flower:   ['Marigold','Rose','Jasmine','Sunflower','Lotus','Hibiscus'],
};
const STATUS_COLORS = {
  growing:       { bg:'var(--color-success-light)', color:'var(--color-success)',  label:' Growing' },
  ready_to_sell: { bg:'var(--color-info-light)',    color:'var(--color-info)',     label:'🟢 Ready' },
  sold:          { bg:'var(--color-warning-light)', color:'var(--color-warning)',  label:'✅ Sold' },
  closed:        { bg:'var(--gray-100)',            color:'var(--gray-500)',       label:'🔒 Closed' },
};

export default function AdminCropPage() {
  const navigate = useNavigate();
  const [tab, setTab]           = useState('crops');   // crops | leads
  const [stats, setStats]       = useState(null);
  const [crops, setCrops]       = useState([]);
  const [leads, setLeads]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage]         = useState(1);
  const [filters, setFilters]   = useState({ status:'', category:'', subCategory:'', search:'', location:'' });
  const [editCrop, setEditCrop] = useState(null);   // crop being status-edited
  const [newStatus, setNewStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { if (tab === 'crops') loadCrops(); else loadLeads(); }, [tab, filters, page]);

  const loadStats = async () => {
    try { const { data } = await api.get('/admin/crops/stats'); setStats(data.stats); }
    catch {}
  };

  const loadCrops = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:20, ...Object.fromEntries(Object.entries(filters).filter(([,v])=>v)) });
      const { data } = await api.get(`/admin/crops?${params}`);
      setCrops(data.crops || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load crops'); }
    finally { setLoading(false); }
  };

  const loadLeads = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/crops/farmer-leads'); setLeads(data.leads || []); }
    catch {} finally { setLoading(false); }
  };

  const setFilter = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };

  const openStatusEdit = (crop) => {
    setEditCrop(crop); setNewStatus(crop.status); setAdminNote('');
  };

  const saveStatus = async () => {
    if (!editCrop) return;
    setSaving(true);
    try {
      await api.put(`/admin/crops/${editCrop._id}/status`, { status: newStatus, adminNote });
      toast.success(`Crop status → ${newStatus} ✅`);
      setCrops(p => p.map(c => c._id === editCrop._id ? { ...c, status: newStatus } : c));
      setEditCrop(null);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const deleteCrop = async (id, name) => {
    if (!window.confirm(`Delete crop "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/crops/${id}`);
      setCrops(p => p.filter(c => c._id !== id));
      setTotal(p => p - 1);
      loadStats();
      toast.success('Crop deleted');
    } catch { toast.error('Error deleting crop'); }
  };

  // Export to CSV (opens as download)
  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
      const { data } = await api.get(`/admin/crops/export?${params}`);
      const rows = data.data;
      if (!rows.length) return toast.error('No data to export');
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h]??'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type:'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a'); a.href = url; a.download = `agri-crops-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} crops`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  // Export to simple HTML then print as PDF
  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
      const { data } = await api.get(`/admin/crops/export?${params}`);
      const rows = data.data;
      if (!rows.length) return toast.error('No data');
      const html = `<html><head><title>Crop Report</title>
        <style>body{font-family:sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}
        th{background:#1A6B3C;color:white;padding:6px 8px;text-align:left}
        td{padding:5px 8px;border-bottom:1px solid #eee}tr:nth-child(even){background:#f9f9f9}
        h2{color:#1A6B3C}@media print{button{display:none}}</style></head>
        <body><h2> Zeva2 — Crop Report</h2>
        <p>Generated: ${new Date().toLocaleString('en-IN')} | Total: ${rows.length} crops</p>
        <table><tr>${Object.keys(rows[0]).map(h=>`<th>${h}</th>`).join('')}</tr>
        ${rows.map(r=>`<tr>${Object.values(r).map(v=>`<td>${v??''}</td>`).join('')}</tr>`).join('')}
        </table><script>window.print()</script></body></html>`;
      const w = window.open('','_blank'); w.document.write(html); w.document.close();
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const STAT_CARDS = stats ? [
    { label:'Total Crops',  value: stats.total,       icon:'', color:'var(--primary)' },
    { label:'Growing',      value: stats.growing,     icon:'', color:'var(--color-success)' },
    { label:'Ready to Sell',value: stats.ready,       icon:'🟢', color:'var(--color-info)' },
    { label:'Sold',         value: stats.sold,        icon:'✅', color:'var(--color-warning)' },
    { label:'Closed',       value: stats.closed,      icon:'🔒', color:'var(--gray-500)' },
    { label:'This Week',    value: stats.newThisWeek, icon:'📅', color:'var(--secondary)' },
  ] : [];

  return (
    <div className="page">
      {/* Header */}
      <div className="header">
        <div style={{ display:'flex', alignItems:'center', gap:'var(--sp-3)', marginBottom:'var(--sp-3)' }}>
          <button className="header__back" onClick={() => navigate('/admin')}>←</button>
          <div style={{ flex:1 }}>
            <div className="header__title"> Crop Management</div>
            <div className="header__subtitle">Admin control for all crops</div>
          </div>
          <button className="header__action" onClick={exportCSV} disabled={exporting}>
            {exporting ? '...' : '📊 CSV'}
          </button>
          <button className="header__action" onClick={exportPDF} disabled={exporting}>
            📄 PDF
          </button>
        </div>

        {/* Stat cards in header */}
        {stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'var(--sp-2)' }}>
            {STAT_CARDS.map(s => (
              <div key={s.label} style={{ background:'rgba(255,255,255,.15)', borderRadius:'var(--r-sm)', padding:'var(--sp-2) var(--sp-3)', textAlign:'center' }}>
                <div style={{ fontSize:18 }}>{s.icon}</div>
                <div style={{ color:'white', fontWeight:700, fontSize:18, lineHeight:1 }}>{s.value}</div>
                <div style={{ color:'rgba(255,255,255,.75)', fontSize:10, marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab${tab==='crops'?' tab--active':''}`} onClick={()=>setTab('crops')}> All Crops <span className="tab__count">{total}</span></button>
        <button className={`tab${tab==='leads'?' tab--active':''}`} onClick={()=>setTab('leads')}>👨‍🌾 Farmer Leads</button>
        <button className={`tab${tab==='category'?' tab--active':''}`} onClick={()=>setTab('category')}>📊 By Category</button>
      </div>

      {/* Filters (only on crops tab) */}
      {tab === 'crops' && (
        <div style={{ background:'white', padding:'var(--sp-3) var(--sp-4)', borderBottom:'1px solid var(--gray-100)' }}>
          {/* Status pills */}
          <div className="pill-bar" style={{ padding:'0 0 var(--sp-2)', background:'transparent' }}>
            {['','growing','ready_to_sell','sold','closed'].map(s => (
              <button key={s} className={`pill${filters.status===s?' pill--active':''}`} onClick={()=>setFilter('status',s)}>
                {s ? STATUS_COLORS[s]?.label : '🌐 All Status'}
              </button>
            ))}
          </div>
          {/* Category pills */}
          <div className="pill-bar" style={{ padding:'0 0 var(--sp-2)', background:'transparent' }}>
            <button className={`pill${!filters.category?' pill--active':''}`} onClick={()=>{ setFilter('category',''); setFilter('subCategory',''); }}>All Categories</button>
            {CATEGORIES.map(c => (
              <button key={c} className={`pill${filters.category===c?' pill--active':''}`} onClick={()=>{ setFilter('category',c); setFilter('subCategory',''); }}>
                {c.charAt(0).toUpperCase()+c.slice(1)}
              </button>
            ))}
          </div>
          {/* Sub-category pills */}
          {filters.category && SUB_CATS[filters.category] && (
            <div className="pill-bar" style={{ padding:'0 0 var(--sp-2)', background:'transparent' }}>
              <button className={`pill${!filters.subCategory?' pill--active':''}`} onClick={()=>setFilter('subCategory','')}>All</button>
              {SUB_CATS[filters.category].map(s => (
                <button key={s} className={`pill${filters.subCategory===s?' pill--active':''}`} onClick={()=>setFilter('subCategory',s)}>{s}</button>
              ))}
            </div>
          )}
          {/* Search + Location */}
          <div style={{ display:'flex', gap:'var(--sp-2)' }}>
            <input className="input" placeholder="🔍 Search crop name..." value={filters.search}
              onChange={e=>setFilter('search',e.target.value)} style={{ flex:2 }} />
            <input className="input" placeholder=" Location..." value={filters.location}
              onChange={e=>setFilter('location',e.target.value)} style={{ flex:1 }} />
          </div>
        </div>
      )}

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>

        {/* ── CROPS TAB ── */}
        {tab === 'crops' && (
          <>
            {loading ? (
              <div className="full-center"><span className="spinner spinner--lg" /></div>
            ) : crops.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state__icon"></div>
                <div className="empty-state__title">No crops found</div>
              </div>
            ) : (
              <>
                <div className="text-sm text-muted" style={{ marginBottom:'var(--sp-3)' }}>
                  Showing {crops.length} of {total} crops
                </div>
                {crops.map(crop => {
                  const st = STATUS_COLORS[crop.status] || STATUS_COLORS.growing;
                  return (
                    <div key={crop._id} className="card" style={{ marginBottom:'var(--sp-3)', borderLeft:`4px solid ${st.color}` }}>
                      <div className="card__body">
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'var(--sp-2)' }}>
                          <div>
                            <div className="fw-bold text-lg">{crop.cropName}</div>
                            <div className="text-sm text-muted">{crop.category} · {crop.subCategory}</div>
                          </div>
                          <span style={{ background:st.bg, color:st.color, borderRadius:'var(--r-full)', padding:'3px 10px', fontSize:12, fontWeight:600, flexShrink:0 }}>{st.label}</span>
                        </div>

                        {/* Farmer info */}
                        <div style={{ background:'var(--gray-50)', borderRadius:'var(--r-sm)', padding:'var(--sp-2) var(--sp-3)', marginBottom:'var(--sp-2)' }}>
                          <div className="text-sm fw-semi">👨‍🌾 {crop.userId?.name || 'Unknown'}</div>
                          <div className="text-xs text-muted">📱 {crop.userId?.mobile} ·  {crop.userId?.locationAddress || '-'}</div>
                        </div>

                        {/* Crop details */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-3)' }}>
                          {[
                            { label:'Day', value: crop.currentDayCount || 0 },
                            { label:'Stages', value: crop.stages?.length || 0 },
                            { label:'Likes', value: crop.likeCount || 0 },
                          ].map(d => (
                            <div key={d.label} style={{ textAlign:'center', background:'var(--gray-50)', borderRadius:'var(--r-sm)', padding:'var(--sp-2)' }}>
                              <div className="fw-bold text-md" style={{ color:'var(--primary)' }}>{d.value}</div>
                              <div className="text-xs text-muted">{d.label}</div>
                            </div>
                          ))}
                        </div>

                        {crop.price && <div className="text-sm" style={{ marginBottom:'var(--sp-2)' }}>💰 ₹{crop.price} {crop.priceUnit?.replace('per_','/ ')}</div>}

                        {/* Actions */}
                        <div className="btn-group" style={{ flexWrap:'wrap' }}>
                          <button className="btn btn--primary btn--sm" onClick={() => openStatusEdit(crop)}>
                            🔄 Change Status
                          </button>
                          <button className="btn btn--outline btn--sm" onClick={() => navigate(`/crop/${crop._id}`)}>
                            👁️ View
                          </button>
                          <button className="btn btn--danger btn--sm" onClick={() => deleteCrop(crop._id, crop.cropName)}>
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {total > 20 && (
                  <div style={{ display:'flex', justifyContent:'center', gap:'var(--sp-2)', margin:'var(--sp-4) 0' }}>
                    <button className="btn btn--outline-gray btn--sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
                    <span className="text-sm text-muted" style={{ alignSelf:'center' }}>Page {page} of {Math.ceil(total/20)}</span>
                    <button className="btn btn--outline-gray btn--sm" disabled={page>=Math.ceil(total/20)} onClick={()=>setPage(p=>p+1)}>Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── FARMER LEADS TAB ── */}
        {tab === 'leads' && (
          <>
            {loading ? <div className="full-center"><span className="spinner spinner--lg" /></div> : (
              leads.map((lead, i) => (
                <div key={lead._id} className="card" style={{ marginBottom:'var(--sp-3)' }}>
                  <div className="card__body">
                    <div style={{ display:'flex', alignItems:'center', gap:'var(--sp-3)', marginBottom:'var(--sp-3)' }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--primary)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, flexShrink:0 }}>
                        {i+1}
                      </div>
                      <div style={{ flex:1 }}>
                        <div className="fw-semi text-base">{lead.farmer?.name || 'Unknown'}</div>
                        <div className="text-xs text-muted">📱 {lead.farmer?.mobile} ·  {lead.farmer?.locationAddress || '-'}</div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div className="fw-bold text-xl" style={{ color:'var(--primary)' }}>{lead.totalCrops}</div>
                        <div className="text-xs text-muted">total crops</div>
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'var(--sp-2)' }}>
                      {[
                        { label:'Growing', value:lead.growing, color:'var(--color-success)' },
                        { label:'Ready',   value:lead.ready,   color:'var(--color-info)' },
                        { label:'Sold',    value:lead.sold,    color:'var(--color-warning)' },
                        { label:'Closed',  value:lead.closed,  color:'var(--gray-400)' },
                      ].map(d => (
                        <div key={d.label} style={{ textAlign:'center', background:'var(--gray-50)', borderRadius:'var(--r-sm)', padding:'var(--sp-2)' }}>
                          <div className="fw-bold" style={{ color:d.color, fontSize:18 }}>{d.value}</div>
                          <div className="text-xs text-muted">{d.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* ── CATEGORY TAB ── */}
        {tab === 'category' && stats && (
          <>
            <div className="fw-semi text-base" style={{ marginBottom:'var(--sp-3)' }}>Crops by Category</div>
            {(stats.byCategory || []).map(cat => {
              const pct = stats.total > 0 ? Math.round((cat.count / stats.total) * 100) : 0;
              return (
                <div key={cat._id} className="card" style={{ marginBottom:'var(--sp-3)', padding:'var(--sp-3) var(--sp-4)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'var(--sp-2)' }}>
                    <span className="fw-semi text-base" style={{ textTransform:'capitalize' }}>{cat._id || 'Unknown'}</span>
                    <span className="fw-bold text-base" style={{ color:'var(--primary)' }}>{cat.count} crops</span>
                  </div>
                  <div className="progress">
                    <div className="progress__bar" style={{ width:`${pct}%` }} />
                  </div>
                  <div className="text-xs text-muted" style={{ marginTop:'var(--sp-1)' }}>{pct}% of total</div>
                </div>
              );
            })}
          </>
        )}

        <div style={{ height:'var(--sp-8)' }} />
      </div>

      {/* ── STATUS EDIT MODAL ── */}
      {editCrop && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={() => setEditCrop(null)}>
          <div style={{ background:'white', borderRadius:'var(--r-lg) var(--r-lg) 0 0', padding:'var(--sp-5)', width:'100%', maxWidth:'var(--max-w)', margin:'0 auto' }}
            onClick={e=>e.stopPropagation()}>

            <div className="fw-semi text-lg" style={{ marginBottom:'var(--sp-1)' }}>🔄 Change Crop Status</div>
            <div className="text-sm text-muted" style={{ marginBottom:'var(--sp-4)' }}>
              {editCrop.cropName} · Farmer: {editCrop.userId?.name}
            </div>

            {/* Status options */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-4)' }}>
              {[
                { value:'growing',       label:'Growing',       desc:'Crop is actively growing' },
                { value:'ready_to_sell', label:'🟢 Ready to Sell', desc:'Available for buyers' },
                { value:'sold',          label:'✅ Sold',           desc:'Crop has been sold' },
                { value:'closed',        label:'🔒 Closed',         desc:'Lifecycle ended (customer support fix)' },
              ].map(s => (
                <button key={s.value} onClick={() => setNewStatus(s.value)}
                  style={{ padding:'var(--sp-3)', borderRadius:'var(--r-sm)', border:`2px solid ${newStatus===s.value?'var(--primary)':'var(--gray-200)'}`, background: newStatus===s.value?'var(--primary-light)':'white', textAlign:'left', cursor:'pointer', transition:'all .15s' }}>
                  <div className="fw-semi text-sm">{s.label}</div>
                  <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>{s.desc}</div>
                </button>
              ))}
            </div>

            {/* Admin note */}
            <div style={{ marginBottom:'var(--sp-4)' }}>
              <label className="label">Admin Note (optional)</label>
              <textarea className="input" rows={2} value={adminNote}
                onChange={e=>setAdminNote(e.target.value)}
                placeholder="Reason for status change e.g. 'Farmer requested closure via support ticket #123'" />
            </div>

            <div className="btn-group">
              <button className="btn btn--primary btn--full" onClick={saveStatus} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save Status'}
              </button>
              <button className="btn btn--outline-gray btn--full" onClick={() => setEditCrop(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
